import { z } from 'zod';

/** Assessor console: cross-org overview, per-org review queues, AI pre-check. */

export const AssessorOverviewSchema = z.object({
  /** Organisations this assessor is assigned to. */
  orgCount: z.number(),
  /** Assessments in scope across all assigned orgs. */
  assessmentCount: z.number(),
  /** Responses finalised and awaiting the assessor's sign-off. */
  awaitingAssessment: z.number(),
  /** Responses the assessor has already marked assessed. */
  assessed: z.number(),
  /** Open assessor comments the assessor has raised. */
  openComments: z.number(),
  perOrg: z.array(
    z.object({
      orgId: z.string(),
      orgName: z.string(),
      assessments: z.number(),
      awaitingAssessment: z.number(),
    }),
  ),
  generatedAt: z.string(),
});
export type AssessorOverview = z.infer<typeof AssessorOverviewSchema>;

export const AssessorReviewItemSchema = z.object({
  responseId: z.string(),
  assessmentId: z.string(),
  assessmentName: z.string(),
  standardName: z.string(),
  clauseNumber: z.string(),
  clauseTitle: z.string(),
  questionText: z.string(),
  status: z.string(),
  reviewState: z.string(),
  hasContent: z.boolean(),
  updatedAt: z.string(),
});
export type AssessorReviewItem = z.infer<typeof AssessorReviewItemSchema>;

export const AssessorOrgReviewsSchema = z.object({
  orgId: z.string(),
  orgName: z.string(),
  items: z.array(AssessorReviewItemSchema),
});
export type AssessorOrgReviews = z.infer<typeof AssessorOrgReviewsSchema>;

export const AssessorReviewsSchema = z.object({
  orgs: z.array(AssessorOrgReviewsSchema),
});
export type AssessorReviews = z.infer<typeof AssessorReviewsSchema>;

/** Result of the AI pre-check on a response. */
export const AiReviewResultSchema = z.object({
  verdict: z.enum(['pass', 'revise']),
  /** Critical gaps that could cause the response to fail assessment. */
  criticalGaps: z.array(z.string()),
  summary: z.string(),
  model: z.string(),
  /** Set when the finding was posted back to the author as a comment. */
  commentId: z.string().nullable(),
});
export type AiReviewResult = z.infer<typeof AiReviewResultSchema>;

export const AiReviewInputSchema = z.object({
  /** Post the finding to the author as a comment (default true when revise). */
  postComment: z.boolean().default(true),
});
export type AiReviewInput = z.infer<typeof AiReviewInputSchema>;
